// WidgetContainer.tsx
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
// Hand-drawn filled magnifier for the "search active" state — Heroicons only
// ships a hollow-ring solid magnifier, which reads the same as the outline.
import { SearchSolidIcon } from "@/ui-components/SearchSolidIcon";
import { AddToTabMenu } from "@/ui-components/AddToTabMenu";
import { Transition } from "@headlessui/react";
import { WidgetHeaderOptions } from "@/widget-components/types";
import { CommonSearchProps } from "@/widget-components/newsquawk-headlines/HeadlinesSearchForm";
import clsx from "clsx";
import { popoutKey, usePopoutWidgetsStore } from "@/stores/PopoutWidgetsStore";
import WidgetErrorBoundary from "../WidgetErrorBoundary";
import { Widget } from "@/utils/widgets";
import { useAccessControl } from "@/hooks/useAccessControl";
import { DemoFirehose, PremiumWidgetPlaceholder, WidgetNoAccess } from "@/widget-components/demo";
import { trackFeatureUsage } from "@/utils/gtm";
import { useWidgetSearchState } from "@/hooks/useWidgetSearchState";
import {
  useIsSpotlit,
  useSpotlightTarget,
  SpotlightControlProvider,
} from "@/contexts/WidgetSpotlightContext";

/** Tracks which widgets have been interacted with this session (resets on page reload). */
const interactedWidgets = new Set<string>();

type WidgetContainerProps = {
  widget: Widget;
  widgetCompositeId?: string;
  onClose: (id: string) => void;
  searchComponent?: React.ComponentType<CommonSearchProps>;

  hideDefaultActions?: boolean;
  children: (args: WidgetHeaderOptions) => ReactNode;
};

export function WidgetContainer(props: WidgetContainerProps) {
  const { authenticated, isSubscriber, isInDemoMode, hasAccessToWidget } = useAccessControl();
  // Null in popout windows — the guide's provider is main-window only.
  const spotlit = useIsSpotlit(props.widgetCompositeId);
  const { targetControl } = useSpotlightTarget();
  const spotlitControl = spotlit ? targetControl : null;
  const hasAccess = hasAccessToWidget(props.widget);

  const addPopoutWidget = usePopoutWidgetsStore((state) => state.addWidget);

  const popoutWidgets = usePopoutWidgetsStore((state) => state.widgets);

  // Identity of *this* instance, so a second copy of the same widget on the tab
  // pops out into its own window instead of sharing the first one's.
  const instanceKey = props.widgetCompositeId ?? props.widget.id;
  const isPoppedOut = popoutWidgets.some((w) => popoutKey(w) === instanceKey);

  const [searchToggle, setSearchToggle] = useState(false);

  // Persisted search params (mirrors widgetFilters) — lets a search stay "in play"
  // after Done and re-run after a tab switch / reload, like filters.
  const [savedSearch, setSavedSearch] = useWidgetSearchState(
    props.widgetCompositeId
  );
  const hasSavedSearch = !!(
    savedSearch.query?.trim() ||
    savedSearch.startDate ||
    savedSearch.endDate
  );
  // Set by the search component while it has results in play (covers the
  // no-persistKey/popout case where the store fallback is component-local).
  const [searchActive, setSearchActive] = useState(false);
  const searchInPlay = searchActive || hasSavedSearch;

  const [actions, setActions] = useState<ReactElement[]>([]);

  const [title, setTitle] = useState(props.widget.name);

  const pushAction = useCallback((action: ReactElement) => {
    setActions((prev) => {
      // Remove existing action with same key (if any), then append the new one
      // this is required to update the action state, and to prevent duplicate actions
      const filtered = prev.filter((a) => a.key !== action.key);
      const newActions = [...filtered, action];

      // Sort actions by key to keep order consistently
      newActions.sort((a, b) => {
        if (a.key && b.key) {
          return a.key.localeCompare(b.key);
        }
        return 0;
      });

      return newActions;
    });
  }, []);

  // Done just collapses the form — the search stays in play (persisted).
  const closeForm = useCallback(() => setSearchToggle(false), []);

  // Dismiss the active search entirely. Called by the filter/highlight/advanced
  // panels when they open, so they aren't hidden behind the persisted search
  // results overlay — mirrors how opening search closes those panels. Clears the
  // saved params + collapses the form so the search component unmounts.
  const closeSearch = useCallback(() => {
    setSearchToggle(false);
    setSearchActive(false);
    setSavedSearch({});
  }, [setSavedSearch]);

  // Notify registered callbacks when search opens
  const searchOpenListeners = useRef<Set<() => void>>(new Set());

  const onSearchOpen = useCallback((callback: () => void) => {
    searchOpenListeners.current.add(callback);
    return () => { searchOpenListeners.current.delete(callback); };
  }, []);

  // When search opens, notify listeners (so filter/highlight panels can close)
  useEffect(() => {
    if (searchToggle) {
      searchOpenListeners.current.forEach((cb) => cb());
    }
  }, [searchToggle]);

  const widgetOptions: WidgetHeaderOptions = useMemo(
    () => ({
      setTitle,
      setActions,
      pushAction,
      closeSearch,
      onSearchOpen,
    }),
    [setTitle, setActions, pushAction, closeSearch, onSearchOpen]
  );

  const handlePopoutClick = useCallback(() => {
    trackFeatureUsage({
      feature_name: "widget",
      feature_action: "popout",
      feature_detail: props.widget.name,
    });
    addPopoutWidget({
      ...props.widget,
      widgetCompositeId: props.widgetCompositeId,
    });
  }, [addPopoutWidget, props.widget, props.widgetCompositeId]);

  const handleFirstInteraction = useCallback(() => {
    if (!((authenticated && hasAccess) || (!authenticated && props.widget.unAuthAccessible))) return;
    const key = props.widgetCompositeId ?? props.widget.id;
    if (interactedWidgets.has(key)) return;
    interactedWidgets.add(key);
    trackFeatureUsage({
      feature_name: "widget_interaction",
      feature_action: "first_interact",
      feature_detail: props.widget.name,
    });
  }, [authenticated, hasAccess, props.widget.unAuthAccessible, props.widgetCompositeId, props.widget.id, props.widget.name]);

  return (
    <SpotlightControlProvider value={spotlitControl}>
    <div
      onPointerDownCapture={handleFirstInteraction}
      className={clsx(
        "h-full flex flex-col relative border bg-white dark:bg-gray-900 rounded-t-md shadow-md dark:shadow-lg dark:shadow-black/20 hover:shadow-xl dark:hover:shadow-black/30 transition-shadow duration-200 overflow-hidden",
        // Guided-demo emphasis. Ring rather than a border swap so the widget
        // doesn't shift by a pixel when it turns on, and z-10 so the ring isn't
        // clipped by the neighbouring grid item.
        spotlit
          ? "nq-demo-ring z-10 border-squawk-blue"
          : "border-gray-200 dark:border-gray-800"
      )}
    >
      {/* Header -- Title and Actions */}
      <div className="relative z-10 bg-gray-100 dark:bg-gray-800/80 text-gray-800 dark:text-white text-2xs px-2 py-0.5 flex justify-between items-center transition-colors select-none">
        {/* Title */}
        <span>{title}</span>

        {/* Actions  */}
        <div className="flex items-center">
          {/* Custom Actions*/}
          {actions.map((action, index) => (
            <div key={action.key ?? index}>{action}</div>
          ))}

          {/* Search Actions */}
          {authenticated && props.searchComponent && (
            <button
              onClick={() => setSearchToggle((pre) => !pre)}
              className={clsx(
                "p-1 no-drag transition-all duration-200",
                // Panel open = solid fill; active+closed = filled blue icon; default = neutral
                searchToggle
                  ? "bg-squawk-blue"
                  : "bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600"
              )}
              title={searchToggle ? "Close search" : "Search"}
            >
              {searchInPlay && !searchToggle ? (
                // Active search (panel closed) — filled blue magnifier, like the filter funnel
                <SearchSolidIcon className="h-3.5 w-3.5 text-squawk-blue" />
              ) : (
                <MagnifyingGlassIcon
                  className={clsx("h-3.5 w-3.5", {
                    "text-white": searchToggle,
                    "text-gray-700 dark:text-white": !searchToggle,
                  })}
                />
              )}
            </button>
          )}

          {/* Default Actions */}
          {!props.hideDefaultActions && (
            <>
              {authenticated && <AddToTabMenu widget={props.widget} />}

              <button
                onClick={handlePopoutClick}
                // Completes the guided demo's popout step (via the
                // feature_usage observer in useStepDetection), so the selector
                // has to survive a header redesign.
                data-analytics-id="widget-popout"
                className={clsx(
                  "p-1 no-drag hover:text-blue-600",
                  spotlitControl === "popout" && "nq-demo-pulse",
                  // isPoppedOut keys on the instance, not the widget id, so
                  // one popped-out copy no longer lights the control on every
                  // other instance of the same widget.
                  isPoppedOut
                    ? "bg-squawk-blue"
                    : "bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600"
                )}
              >
                <ArrowTopRightOnSquareIcon className="text-gray-700 dark:text-white h-3.5 w-3.5" />
              </button>

              <button
                onClick={() => props.onClose(props.widget.id)}
                className="p-1 no-drag bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-blue-600"
              >
                <XMarkIcon className="text-gray-700 dark:text-white h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      <WidgetErrorBoundary>
        {/* widget body */}
        <div className="relative flex-1 flex flex-col overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-500 min-h-full no-drag pb-[24px] widget-content-scroll select-text">
          {/** Search component stays mounted while a search is in play (form hidden)
           *  so results persist after Done and re-run on reload, like filters. */}
          {(searchToggle || searchInPlay) && props.searchComponent && (
            <div className="z-50 flex flex-col">
              <props.searchComponent
                setTitle={setTitle}
                onClose={closeForm}
                formOpen={searchToggle}
                persistKey={props.widgetCompositeId}
                onActiveChange={setSearchActive}
              />
            </div>
          )}

          <section className="relative min-h-0 flex-1 flex flex-col">
            {/* 1. Full widget: has access OR unAuthAccessible when unauthenticated */}
            {((authenticated && hasAccess) ||
              (!authenticated && props.widget.unAuthAccessible)) &&
              props.children(widgetOptions)}

            {/* 2. Active subscriber without required role for this widget → upgrade plan */}
            {authenticated && isSubscriber && !isInDemoMode && !hasAccess && (
              <WidgetNoAccess widgetName={props.widget.name} />
            )}

            {/* 3. Demo mode (expired access, no subscription, or unauthenticated) + demoAccessible → demo */}
            {isInDemoMode && !hasAccess && props.widget.demoAccessible && (
              <DemoFirehose
                widgetId={props.widget.id}
                widgetCompositeId={props.widgetCompositeId}
                headerOptions={widgetOptions}
              />
            )}

            {/* 4. Demo mode + not demoAccessible + not unAuthAccessible → premium placeholder */}
            {isInDemoMode && !hasAccess && !props.widget.demoAccessible &&
              !((!authenticated && props.widget.unAuthAccessible)) && (
              <PremiumWidgetPlaceholder widgetName={props.widget.name} />
            )}
          </section>
        </div>
      </WidgetErrorBoundary>
    </div>
    </SpotlightControlProvider>
  );
}
